import React, { useCallback, useMemo, useRef, useState } from "react";
import { useGetRecentDataBatched } from "api/api-hooks/datasets-hooks";
import { ensureKeysWithUUID } from "utils/utils";
import { DatasetDataInfoResponse } from "api/model/dataset/DatasetDataInfoResponse";
import { APIResponse } from "services/HttpService";
import { isEqual } from "lodash-es";
import UniversalDatasetTableRow from "./UniversalDatasetTableRow";
import UniversalDatasetTableHeaderRow from "./UniversalDatasetTableHeaderRow";
import { getUniversalTableDatasetValues } from "./universal-dataset-table-utils";
import { IUniversalDatasetTableConfig } from "./UniversalDatasetTableTypes";
import "./UniversalDatasetTable.scss";

export default function UniversalDatasetTable(props: IUniversalDatasetTableConfig) {
  const [selectedRowIndex, setSelectedRowIndex] = useState<number | null>(null);

  const lastDataRef = useRef<APIResponse<DatasetDataInfoResponse[]>[]>([]);
  const lastDatasetsRef = useRef<DatasetDataInfoResponse[]>([]);

  const datasetIds = useMemo(() => getUniversalTableDatasetValues(props.rows), [props.rows]);

  const onRowClickHandler = useCallback((index: number) => {
    setSelectedRowIndex(prev => (prev === index ? null : index));
  }, []);

  const { data: recentData } = useGetRecentDataBatched(datasetIds, {
    count: 1,
    refTime: props.referenceTime && props.referenceTime,
    options: {
      refetchInterval: props.updateFrequency ? props.updateFrequency * 1000 : 0,
    },
  });

  const allDatasets = useMemo(() => {
    if (!isEqual(recentData, lastDataRef.current)) {
      lastDataRef.current = recentData;
      lastDatasetsRef.current = recentData.flatMap(batch => batch?.data ?? []);
    }
    return lastDatasetsRef.current;
  }, [recentData]);

  const rowsWithUUID = useMemo(() => {
    return ensureKeysWithUUID("uuid", props.rows);
  }, [props.rows]);

  const renderedRows = useMemo(() => {
    return rowsWithUUID.map((row, index) => {
      return (
        <UniversalDatasetTableRow
          key={row.uuid}
          rowIndex={index}
          recentData={allDatasets}
          rowData={row}
          columnsOptions={props.columnsOptions}
          compactMode={props.compactMode}
          maxAge={props.maxAge}
          isSelected={selectedRowIndex === index}
          onClick={onRowClickHandler}
        />
      );
    });
  }, [
    rowsWithUUID,
    allDatasets,
    props.columnsOptions,
    props.compactMode,
    props.maxAge,
    selectedRowIndex,
    onRowClickHandler,
  ]);

  return (
    <div className="main-main-window">
      {props.rows.length > 0 && (
        <table className="main-window">
          <thead>
            {props.showColumnHeaders && !props.hideHeader && (
              <UniversalDatasetTableHeaderRow columnsOptions={props.columnsOptions} />
            )}
          </thead>
          <tbody className="universal-table-body">{renderedRows}</tbody>
        </table>
      )}
    </div>
  );
}
